import React, { useState, useEffect } from 'react';
import { getAllSavedContent, getImageUrl } from '../services/api';
import { articleUrl } from '../services/slugify';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';

function HomePage() {
  const [contents, setContents] = useState([]);
  const [filteredContents, setFilteredContents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      const response = await getAllSavedContent();
      if (response.success) {
        setContents(response.data);
        setFilteredContents(response.data);
      }
    } catch (error) {
      console.error('Failed to load content:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} mins ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const extractFirstImage = (htmlContent, savedImageUrl) => {
    if (savedImageUrl) return getImageUrl(savedImageUrl);
    if (!htmlContent) return null;
    
    const imgMatch = htmlContent.match(/<img[^>]+src=["']([^"']+)["']/i);
    if (imgMatch && imgMatch[1]) return imgMatch[1];
    
    const ogImageMatch = htmlContent.match(/<meta[^>]+property=["']og:image["'][^>]+content=["']([^"']+)["']/i);
    if (ogImageMatch && ogImageMatch[1]) return ogImageMatch[1];
    
    return null;
  };

  const estimateReadTime = (htmlContent) => {
    if (!htmlContent) return 1;
    // Remove style tags first, then strip HTML tags
    const cleanedHtml = htmlContent.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
    const text = cleanedHtml.replace(/<[^>]*>/g, '');
    const words = text.split(/\s+/).length;
    return Math.ceil(words / 200);
  };

  const extractExcerpt = (htmlContent, maxLength = 150) => {
    if (!htmlContent) return '';
    // Remove style, script, and other unwanted tags before extracting text
    let cleaned = htmlContent.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
    cleaned = cleaned.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
    cleaned = cleaned.replace(/<meta[^>]*>/gi, '');
    cleaned = cleaned.replace(/<link[^>]*>/gi, '');
    
    const text = cleaned.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  if (loading) {
    return (
      <div className="news-portal">
        <SiteHeader activeSlug="all" />
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading news...</p>
        </div>
      </div>
    );
  }

  // Use section-based layout if admin has saved a custom layout,
  // otherwise fall back to index-based layout for backward compatibility.
  const hasCustomLayout = filteredContents.some(
    (c) => c.sortOrder !== 9999 || c.section !== 'latest_news'
  );

  let mainStory, topStories, sidebarStories, gridStories, bottomStories;

  if (hasCustomLayout) {
    const featured = filteredContents
      .filter((c) => c.section === 'featured')
      .sort((a, b) => a.sortOrder - b.sortOrder);
    const headlines = filteredContents
      .filter((c) => c.section === 'more_headlines')
      .sort((a, b) => a.sortOrder - b.sortOrder);
    const latest = filteredContents
      .filter((c) => c.section === 'latest_news')
      .sort((a, b) => a.sortOrder - b.sortOrder);
    const bottom = filteredContents
      .filter((c) => c.section === 'bottom')
      .sort((a, b) => a.sortOrder - b.sortOrder);

    mainStory = featured[0];
    topStories = featured.slice(1, 5);
    sidebarStories = headlines;
    gridStories = latest;
    bottomStories = bottom;
  } else {
    mainStory = filteredContents[0];
    topStories = filteredContents.slice(1, 5);
    sidebarStories = filteredContents.slice(5, 10);
    gridStories = filteredContents.slice(10, 22);
    bottomStories = filteredContents.slice(22);
  }

  return (
    <div className="news-portal">
      <SiteHeader activeSlug="all" />

      {/* Main Content */}
      <main className="news-main">
        <div className="container">
          <div className="news-layout">
            {/* Main Content Column */}
            <div className="main-column">
              {/* Hero Story */}
              {mainStory && (
                <article className="hero-story">
                  <a href={`/${articleUrl(mainStory.title, mainStory.id, contents)}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                    {extractFirstImage(mainStory.htmlContent, mainStory.imageUrl) && (
                      <div className="hero-image">
                        <img src={extractFirstImage(mainStory.htmlContent, mainStory.imageUrl)} alt={mainStory.title} />
                      </div>
                    )}
                    <div className="hero-content">
                      <h2 className="hero-title">{mainStory.title}</h2>
                      <p className="hero-excerpt">{extractExcerpt(mainStory.htmlContent, 200)}</p>
                      <div className="hero-meta">
                        <span className="meta-time">{formatDate(mainStory.createdAt)}</span>
                      </div>
                    </div>
                  </a>
                </article>
              )}

              {/* Top Stories Grid */}
              {topStories.length > 0 && (
                <div className="top-stories-grid">
                  {topStories.map((story) => (
                    <article key={story.id} className="story-card-horizontal">
                      <a href={`/${articleUrl(story.title, story.id, contents)}`} style={{ textDecoration: 'none', color: 'inherit', display: 'flex', width: '100%', gap: '15px' }}>
                        {extractFirstImage(story.htmlContent, story.imageUrl) && (
                          <div className="story-image-horizontal">
                            <img src={extractFirstImage(story.htmlContent, story.imageUrl)} alt={story.title} />
                          </div>
                        )}
                        <div className="story-content-horizontal">
                          <h3 className="story-title-horizontal">{story.title}</h3>
                          <p className="story-excerpt-small">{extractExcerpt(story.htmlContent, 100)}</p>
                          <span className="story-time">{formatDate(story.createdAt)}</span>
                        </div>
                      </a>
                    </article>
                  ))}
                </div>
              )}

              {/* More Stories Section */}
              <section className="section-header">
                <h2 className="section-title">Latest News</h2>
              </section>

              <div className="stories-grid">
                {gridStories.map((story) => (
                  <article key={story.id} className="story-card">
                    <a href={`/${articleUrl(story.title, story.id, contents)}`} style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column', height: '100%' }}>
                      {extractFirstImage(story.htmlContent, story.imageUrl) && (
                        <div className="story-image">
                          <img src={extractFirstImage(story.htmlContent, story.imageUrl)} alt={story.title} />
                        </div>
                      )}
                      <div className="story-content">
                        <h3 className="story-title">{story.title}</h3>
                        <p className="story-excerpt">{extractExcerpt(story.htmlContent, 120)}</p>
                        <span className="story-time">{formatDate(story.createdAt)}</span>
                      </div>
                    </a>
                  </article>
                ))}
              </div>

              {/* Bottom Stories List */}
              {bottomStories.length > 0 && (
                <div className="bottom-stories">
                  {bottomStories.map((story) => (
                    <article key={story.id} className="story-list-item">
                      <a href={`/${articleUrl(story.title, story.id, contents)}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                        <h4 className="story-list-title">{story.title}</h4>
                        <span className="story-time">{formatDate(story.createdAt)}</span>
                      </a>
                    </article>
                  ))}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="sidebar-column">
              {/* Featured Topic Section */}
              {topStories.length > 0 && (
                <div className="sidebar-section">
                  <h3 className="sidebar-title">Featured Topic</h3>
                  <div className="sidebar-stories">
                    {topStories.slice(0, 1).map((story) => (
                      <article key={story.id} className="sidebar-story-featured">
                        <a href={`/${articleUrl(story.title, story.id, contents)}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                          {extractFirstImage(story.htmlContent, story.imageUrl) && (
                            <div className="sidebar-featured-image">
                              <img src={extractFirstImage(story.htmlContent, story.imageUrl)} alt={story.title} />
                            </div>
                          )}
                          <div className="sidebar-story-content">
                            <h4 className="sidebar-story-title">{story.title}</h4>
                            <p className="sidebar-excerpt">{extractExcerpt(story.htmlContent, 80)}</p>
                          </div>
                        </a>
                      </article>
                    ))}
                    {topStories.slice(1, 3).map((story) => (
                      <article key={story.id} className="sidebar-story-with-image">
                        <a href={`/${articleUrl(story.title, story.id, contents)}`} style={{ textDecoration: 'none', color: 'inherit', display: 'flex', gap: '12px' }}>
                          {extractFirstImage(story.htmlContent, story.imageUrl) && (
                            <div className="sidebar-thumb">
                              <img src={extractFirstImage(story.htmlContent, story.imageUrl)} alt={story.title} />
                            </div>
                          )}
                          <div className="sidebar-story-content">
                            <h4 className="sidebar-story-title">{story.title}</h4>
                          </div>
                        </a>
                      </article>
                    ))}
                  </div>
                </div>
              )}

              {/* More Headlines Section */}
              <div className="sidebar-section">
                <h3 className="sidebar-title">More Headlines</h3>
                <div className="sidebar-stories">
                  {sidebarStories.slice(0, 5).map((story) => (
                    <article key={story.id} className="sidebar-story-with-image">
                      <a href={`/${articleUrl(story.title, story.id, contents)}`} style={{ textDecoration: 'none', color: 'inherit', display: 'flex', gap: '12px' }}>
                        {extractFirstImage(story.htmlContent, story.imageUrl) && (
                          <div className="sidebar-thumb">
                            <img src={extractFirstImage(story.htmlContent, story.imageUrl)} alt={story.title} />
                          </div>
                        )}
                        <div className="sidebar-story-content">
                          <h4 className="sidebar-story-title">{story.title}</h4>
                        </div>
                      </a>
                    </article>
                  ))}
                </div>
              </div>

              {/* Opinion Section */}
              <div className="sidebar-section">
                <h3 className="sidebar-title">Opinion</h3>
                <div className="sidebar-stories">
                  {gridStories.slice(0, 3).map((story) => (
                    <article key={story.id} className="sidebar-story">
                      <a href={`/${articleUrl(story.title, story.id, contents)}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                        <div className="sidebar-story-content">
                          <h4 className="sidebar-story-title">{story.title}</h4>
                        </div>
                      </a>
                    </article>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

export default HomePage;
